import React from 'react';
import { gql, useMutation } from '@apollo/client';
import pMap from 'p-map';
// qa-agent's introspection snapshot predates `projectId` on the upload input
// and the text attachment enum values, so the request shapes come from
// react-client's generated schema and the document is declared here rather
// than generated.
import type { PrivatePostFileUploadUrlInput } from '@lifeomic/react-client/generated/graphql/graphql';
import type {
  AttachmentType,
  NewAttachment,
} from '../__generated__/api.graphql';
import { getAttachmentUploadConfig } from '../utils/chatAttachments';

const CREATE_PRIVATE_POST_ATTACHMENT = gql`
  mutation CreatePrivatePostAttachment($input: PrivatePostFileUploadUrlInput!) {
    privatePostFileUploadUrl(input: $input) {
      id
      fileLocation {
        uploadUrl
      }
    }
  }
`;

type CreatePrivatePostAttachmentData = {
  privatePostFileUploadUrl?: {
    id: string;
    fileLocation?: { uploadUrl?: string | null } | null;
  } | null;
};

type CreatePrivatePostAttachmentVariables = {
  input: PrivatePostFileUploadUrlInput;
};

/** A file the clinician has staged in the composer but not yet sent. */
export type StagedAttachment = {
  /** Client-side id; stable across re-renders so chips can be keyed and removed. */
  id: string;
  file: File;
  contentType: string;
};

/**
 * Uploads run in parallel but stay bounded: a clinician attaching several
 * multi-megabyte PDFs at once should not open an unbounded number of PUTs.
 */
const UPLOAD_CONCURRENCY = 3;

export class UnsupportedAttachmentError extends Error {
  constructor(contentType: string) {
    super(`Unsupported attachment content type: ${contentType}`);
    this.name = 'UnsupportedAttachmentError';
  }
}

export class AttachmentUploadError extends Error {
  constructor(fileName: string, options?: { cause?: unknown }) {
    super(`Failed to upload attachment: ${fileName}`, options);
    this.name = 'AttachmentUploadError';
  }
}

export type UploadChatAttachments = (
  attachments: StagedAttachment[],
) => Promise<NewAttachment[]>;

/**
 * Turns staged files into the `attachmentsV2` payload for `createPrivatePost`.
 *
 * Two steps per file, mirroring the member app's forum upload strategy: ask
 * forum-service for a presigned URL, then PUT the bytes to it. The post is only
 * created once every upload resolves, so a message never references a file that
 * is not there.
 */
export const useChatAttachmentUpload = ({
  userIds,
  projectId,
}: {
  userIds: string[];
  projectId?: string;
}) => {
  const [createAttachmentMutation] = useMutation<
    CreatePrivatePostAttachmentData,
    CreatePrivatePostAttachmentVariables
  >(CREATE_PRIVATE_POST_ATTACHMENT);

  const uploadAttachments = React.useCallback<UploadChatAttachments>(
    async attachments => {
      if (attachments.length === 0) {
        return [];
      }

      return pMap(
        attachments,
        async ({ id, file, contentType }) => {
          const uploadConfig = getAttachmentUploadConfig(contentType);
          if (!uploadConfig) {
            throw new UnsupportedAttachmentError(contentType);
          }

          try {
            const { data } = await createAttachmentMutation({
              variables: {
                input: {
                  userIds,
                  fileType: uploadConfig.fileType,
                  ...(projectId ? { projectId } : {}),
                },
              },
            });

            const upload = data?.privatePostFileUploadUrl;
            if (!upload?.fileLocation?.uploadUrl) {
              throw new Error('No upload URL returned by forum-service');
            }

            const response = await fetch(upload.fileLocation.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': contentType },
            });

            if (!response.ok) {
              throw new Error(
                `Upload responded ${response.status} ${response.statusText}`,
              );
            }

            return {
              id,
              externalId: upload.id,
              // Both enums serialize to the same strings; qa-agent's generated
              // one is just missing CSV, TXT, JSON, and MD. Widening here keeps
              // the rest of the chat on its existing generated types.
              type: uploadConfig.attachmentType as AttachmentType,
              subType: 'file-service',
              // The agent infers a text attachment's mime type from the file
              // extension, so the original name has to survive the round trip
              // or .txt and .md content is silently dropped from the prompt.
              fileName: file.name,
            };
          } catch (error) {
            if (error instanceof UnsupportedAttachmentError) {
              throw error;
            }
            throw new AttachmentUploadError(file.name, { cause: error });
          }
        },
        { concurrency: UPLOAD_CONCURRENCY },
      );
    },
    [createAttachmentMutation, projectId, userIds],
  );

  return { uploadAttachments };
};
